<script setup lang="ts">
  // Desktop V2PrintingPreferencesTab blueprint over the native name-based
  // store: Asignaciones (slot rows with live health chips) + Condiciones
  // (trigger/source per destination). Codepage pickers and the sticker
  // settings shortcut moved OUT to the per-printer edit modal — the sticker
  // slot keeps a "Configurar etiquetas" shortcut that opens the assigned
  // printer's edit modal.
  import { computed, getCurrentInstance } from 'vue';
  import { PrintSource, TicketPrintSetting } from '@/enums/PrintEnum';
  import { useModal } from '@/composition/use-modal';
  import { useNativePrintingUi } from '@/composition/printing/use-native-printing-ui';
  import {
    buildPrintSourceItems,
    buildPrintTriggerItems,
  } from '@/composition/printing/print-condition-items';
  import { IPrinter } from '@/interfaces/IPrinter';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiListbox, {
    IPosUiListboxItem,
  } from '@/components/pos-v2/ui/PosUiListbox.vue';
  import V2NativePrinterEditModal from './V2NativePrinterEditModal.vue';

  type TDestination = 'ticket' | 'comanda' | 'sticker';

  const modals = useModal();
  const nativeUi = useNativePrintingUi();
  const printing = nativeUi.printing;
  const store = nativeUi.store;
  const restaurantStore = nativeUi.restaurantStore;

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  // #region assignment writes (name-based store, merged list incl. network)

  const printerItems = computed<IPosUiListboxItem[]>(() =>
    (printing.printers.value ?? []).map((p) => ({ id: p.id, label: p.name }))
  );

  function findPrinter(id: number | string | null): IPrinter | null {
    if (id === null) return null;
    return (printing.printers.value ?? []).find((p) => p.id === id) ?? null;
  }

  // Clears go through clearAssignment: persist()'s name retention makes
  // set*Printer(null) resurrect the old assignment on the next discovery
  // pass (the persisted *PrinterName survives and re-resolves by name).
  function setAssignment(
    destination: TDestination,
    stationId: number | null,
    id: number | string | null
  ) {
    const printer = findPrinter(id);
    if (destination === 'ticket') {
      if (printer) printing.setTicketPrinter(printer);
      else store.clearAssignment('ticket');
    } else if (destination === 'sticker') {
      if (printer) printing.setStickerPrinter(printer);
      else store.clearAssignment('sticker');
    } else if (stationId !== null) {
      if (printer) printing.setComandaPrinter(stationId, printer);
      else store.clearAssignment('comanda', stationId);
    }
  }

  // #endregion

  // #region slot rows + health chips

  interface ISlotChip {
    color: 'success' | 'error' | 'warning' | 'info' | 'neutral';
    labelKey: string;
  }

  interface IAssignmentSlot {
    key: string;
    destination: TDestination;
    stationId: number | null;
    label: string;
    assignedName: string | null;
    assignedId: number | string | null;
    chip: ISlotChip;
  }

  const assignmentSlots = computed<IAssignmentSlot[]>(() => {
    const slots: IAssignmentSlot[] = [];

    function slotOf(
      key: string,
      destination: TDestination,
      stationId: number | null,
      label: string,
      livePrinter: IPrinter | null
    ): IAssignmentSlot {
      const assignedName =
        livePrinter?.name ??
        nativeUi.assignedNameOf(destination, stationId ?? undefined);
      return {
        key,
        destination,
        stationId,
        label,
        assignedName,
        assignedId: livePrinter?.id ?? null,
        chip: nativeUi.slotHealthChipOf(destination, assignedName),
      };
    }

    slots.push(
      slotOf(
        'ticket',
        'ticket',
        null,
        i18nT('printingV2.destTicket'),
        printing.ticketPrinter.value
      )
    );

    for (const station of restaurantStore.printStations) {
      slots.push(
        slotOf(
          `comanda:${station.stationId}`,
          'comanda',
          station.stationId,
          station.name,
          printing.comandaPrintersByStation.value[station.stationId] ?? null
        )
      );
    }

    slots.push(
      slotOf(
        'sticker',
        'sticker',
        null,
        i18nT('printingV2.destSticker'),
        printing.stickerPrinter.value
      )
    );

    return slots;
  });

  /** Sticker-slot shortcut into the assigned printer's edit modal. */
  function openStickerPrinterEdit() {
    const name =
      printing.stickerPrinter.value?.name ?? nativeUi.assignedNameOf('sticker');
    if (!name) return;
    modals.show(
      V2NativePrinterEditModal,
      { printerName: name },
      {
        adaptive: true,
        height: 'auto',
        width: 560,
        maxWidth: window.innerWidth * 0.95,
        scrollable: true,
        name: 'v2-native-printer-edit-modal',
      },
      {}
    );
  }

  const stickerAssignedName = computed(
    () =>
      printing.stickerPrinter.value?.name ?? nativeUi.assignedNameOf('sticker')
  );

  // #endregion

  // #region trigger / source items

  // One shared list per field for every destination — see print-condition-items.ts.
  const triggerItems = computed<IPosUiListboxItem[]>(() =>
    buildPrintTriggerItems(i18nT)
  );
  const sourceItems = computed<IPosUiListboxItem[]>(() =>
    buildPrintSourceItems(i18nT)
  );

  interface IConditionsGroup {
    key: TDestination;
    title: string;
    settings: { trigger: TicketPrintSetting; source: PrintSource };
  }

  const conditionsGroups = computed<IConditionsGroup[]>(() => [
    {
      key: 'ticket',
      title: i18nT('printingV2.destTicket'),
      settings: printing.ticketPrintingSettings.value,
    },
    {
      key: 'comanda',
      title: i18nT('printingV2.sectionComandas'),
      settings: printing.comandaPrintingSettings.value,
    },
    {
      key: 'sticker',
      title: i18nT('printingV2.destSticker'),
      settings: printing.stickerPrintingSettings.value,
    },
  ]);

  // Writes mutate the store's reactive settings objects directly: the
  // store-wide watcher persists them (same mechanism the legacy modal used).
  function setTrigger(
    settings: { trigger: TicketPrintSetting },
    value: number | string | null
  ) {
    if (value === null) return;
    settings.trigger = Number(value) as TicketPrintSetting;
  }

  function setSource(
    settings: { source: PrintSource },
    value: number | string | null
  ) {
    if (value === null) return;
    settings.source = Number(value) as PrintSource;
  }

  // #endregion
</script>

<template>
  <div class="v2-native-preferences-tab">
    <!-- Asignaciones: one row per slot with a live health chip -->
    <section class="preferences-section">
      <h4 class="section-title">{{ $t('printingV2.sectionAssignments') }}</h4>
      <div class="assignment-rows">
        <div
          v-for="slot in assignmentSlots"
          :key="slot.key"
          class="assignment-row"
        >
          <div class="assignment-row-head">
            <span class="assignment-row-label">{{ slot.label }}</span>
            <PosUiBadge :color="slot.chip.color" variant="soft" size="xs">
              {{ $t(slot.chip.labelKey) }}
            </PosUiBadge>
          </div>
          <div class="assignment-row-controls">
            <PosUiListbox
              :value="slot.assignedId"
              :items="printerItems"
              clearable
              :placeholder="i18nT('printingV2.noPrinterOption')"
              class="assignment-row-listbox"
              @input="setAssignment(slot.destination, slot.stationId, $event)"
            />
            <PosUiButton
              v-if="slot.destination === 'sticker'"
              color="neutral"
              variant="outline"
              size="sm"
              icon="tune"
              :disabled="!stickerAssignedName"
              @click="openStickerPrinterEdit"
            >
              {{ $t('printingV2.nativeStickerConfig') }}
            </PosUiButton>
          </div>
        </div>
      </div>
    </section>

    <!-- Condiciones: trigger + source per destination -->
    <section class="preferences-section">
      <h4 class="section-title">{{ $t('printingV2.sectionConditions') }}</h4>
      <div class="conditions-grid">
        <div
          v-for="group in conditionsGroups"
          :key="group.key"
          class="conditions-group"
        >
          <div class="conditions-group-title">{{ group.title }}</div>
          <div class="section-field">
            <label class="section-label">{{
              $t('printingV2.fieldTrigger')
            }}</label>
            <PosUiListbox
              :value="group.settings.trigger"
              :items="triggerItems"
              @input="setTrigger(group.settings, $event)"
            />
          </div>
          <div class="section-field">
            <label class="section-label">{{
              $t('printingV2.fieldSource')
            }}</label>
            <PosUiListbox
              :value="group.settings.source"
              :items="sourceItems"
              @input="setSource(group.settings, $event)"
            />
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped lang="scss">
  .v2-native-preferences-tab {
    @apply tw-flex tw-flex-col tw-gap-4;

    .preferences-section {
      @apply tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-white tw-p-4;

      .section-title {
        @apply tw-m-0 tw-mb-3 tw-text-sm tw-font-bold tw-text-neutral-900;
      }
    }

    .assignment-rows {
      @apply tw-flex tw-flex-col tw-gap-2.5;

      .assignment-row {
        @apply tw-flex tw-flex-col tw-gap-1.5 tw-rounded-lg tw-border tw-border-neutral-100 tw-p-3 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between;

        .assignment-row-head {
          @apply tw-flex tw-min-w-0 tw-items-center tw-gap-2;

          .assignment-row-label {
            @apply tw-truncate tw-text-sm tw-font-medium tw-text-neutral-800;
          }
        }

        .assignment-row-controls {
          @apply tw-flex tw-flex-col tw-gap-2 sm:tw-flex-row sm:tw-items-center;

          .assignment-row-listbox {
            @apply tw-w-full sm:tw-w-64;
          }
        }
      }
    }

    .conditions-grid {
      @apply tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-3;

      .conditions-group {
        @apply tw-flex tw-flex-col tw-gap-3;

        .conditions-group-title {
          @apply tw-text-xs tw-font-bold tw-uppercase tw-tracking-wide tw-text-neutral-400;
        }
      }
    }

    .section-field {
      @apply tw-flex tw-flex-col tw-gap-1.5;

      .section-label {
        @apply tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-neutral-500;
      }
    }
  }
</style>
